import { useMemo } from 'react';
import { DomainsStatsResponse } from '../../../shared/models/monitor/data/sources/DomainsStatsResponse';
import { DomainStats } from '../../../shared/types/monitor/data/sources/DomainStats';
import { calculatePercentageShare } from '../../../shared/helpers/StatsHelpers';

export type SourceStatsWithShare = {
  source: string;
  count: number;
  promptCount?: number;
  share: number;
  shareChange?: number;
  promptCountChange?: number;
};

export type DomainStatsWithShare = Omit<DomainStats, 'sources' | 'shareChange'> & {
  share: number;
  sources?: SourceStatsWithShare[];
};

export type DomainsWithSourcesStatsWithShare = {
  domains: DomainStatsWithShare[];
  pagination?: DomainsStatsResponse['pagination'];
  totalCount?: number;
};

/**
 * Hook to calculate share percentages for domains with sources
 * Uses totalCount (sum of all mentions across entire dataset) for accurate share percentages
 */
export const useDomainsWithSourcesStatsShare = (
  stats: DomainsStatsResponse | undefined
): DomainsWithSourcesStatsWithShare | undefined => {
  return useMemo(() => {
    if (!stats?.domains) {
      return stats
        ? { domains: [], pagination: stats.pagination, totalCount: stats.totalCount }
        : undefined;
    }

    // Calculate total for share calculations
    // Use totalCount (sum of all mentions across entire dataset) for accurate share percentages
    // totalCount is ONLY for share calculations, not for pagination
    const calculateTotalFromDomains = (domains: typeof stats.domains) => {
      return domains.reduce((sum, domain) => sum + domain.count, 0);
    };

    // Use totalCount if available, otherwise fallback to sum of displayed domains
    // Do NOT use pagination?.totalItems for share calculations (it's only for pagination)
    const total = stats.totalCount ?? calculateTotalFromDomains(stats.domains);
    const safeTotal = total > 0 ? total : 1;

    // Calculate share for each domain and its sources
    const domainsWithShare: DomainStatsWithShare[] = stats.domains.map((domain) => ({
      ...domain,
      share: parseFloat(calculatePercentageShare(domain.count, safeTotal, false)),
      sources: domain.sources?.map((source) => ({
        ...source,
        share: parseFloat(calculatePercentageShare(source.count, safeTotal, false)),
      })),
    }));

    return {
      domains: domainsWithShare,
      pagination: stats.pagination,
      totalCount: stats.totalCount,
    };
  }, [stats]);
};
